import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { useIsMobile } from "@circleco/compass/hooks/useIsMobile";
import { createRootRouteWithContext } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type { QueryClient } from "react-query";

import { NavigationTracker } from "../components/AnalyticsTracker/NavigationTracker";
import { MainAppContent } from "../components/MainAppContent";
import { ElectronAppWrapper } from "../components/MainAppContent/ElectronAppWrapper";
import { HighlightStylesByAppearance } from "../components/shared/HighlightJs/HighlightStylesByAppearance";
import { HEADLESS_UI_POPOVER_ROOT } from "../components/shared/uikit/PopoverPortal";
import type { PunditUserContext } from "../contexts/punditUserContext";
import {
  isVersionGreaterThanOrEqual,
  useElectronAPI,
} from "../hooks/utils/useElectronAPI";
import { useContentProps } from "../providers/MainApp/ContentPropsProvider";
import { RouteChangeEventWrapper } from "../providers/MainApp/RouteChangeEventWrapper";
import { MultiContextProvider } from "../providers/MultiContextProvider";

/**
 * Router root. Holds only globals: shared providers and `MainAppContent` (which
 * renders the outlet).
 *
 * Layout note (intentional, transitional): the member-area AppShell is rendered
 * by `MainAppContent` *above* the outlet, so it currently wraps every route.
 * Full-screen routes (the `_minimal` segment, e.g. Live) opt out at runtime via
 * `useIsMinimalLayoutRoute` inside `MainAppContent` — a bridge needed only
 * because the shell is an ancestor of the route tree rather than a node in it.
 *
 * End state ("_shell lift"): move the shell out of here into a sibling pathless
 * `_shell` layout route (`_shell/route.tsx`), alongside `_minimal`/`_embed`. The
 * router then selects layout from the route tree, and both the `MainAppContent`
 * branch and `useIsMinimalLayoutRoute` go away. See the migration rationale in
 * `CircleAIAppShellLayout/ShellBypassContext.tsx`.
 */
const RootLayout = () => {
  const { isElectron, electronVersion } = useElectronAPI();
  const shouldUseElectronWrapper =
    isElectron && isVersionGreaterThanOrEqual(String(electronVersion), "0.1.2");
  const contentProps = useContentProps();

  const isMobile = useIsMobile();

  const ContentComponent = shouldUseElectronWrapper
    ? ElectronAppWrapper
    : MainAppContent;

  return (
    <RouteChangeEventWrapper>
      <NavigationTracker>
        <HighlightStylesByAppearance />
        <div
          id={HEADLESS_UI_POPOVER_ROOT}
          className="bg-secondary"
          data-testid="main-app-provider"
        >
          <MultiContextProvider>
            {/* Keep this root route free of `validateSearch`: declaring one
                types the router's search globally and breaks the many call
                sites that pass literal search objects to `navigate()`. The
                global CircleAI URL contract is read from here. */}
            <CircleAIAppShell.Provider
              defaultState={{
                mode: "community",
                dockMode: "collapsed",
                conversationPanelState: "closed",
                isLeftSidebarVisible: true,
                isRightSidebarVisible: false,
              }}
            >
              <ContentComponent {...contentProps} />
            </CircleAIAppShell.Provider>
          </MultiContextProvider>
          {!isMobile && <TanStackRouterDevtools position="bottom-right" />}
        </div>
      </NavigationTracker>
    </RouteChangeEventWrapper>
  );
};

export interface RootRouteContext {
  isMobile: boolean;
  punditUserContext: PunditUserContext | undefined;
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RootRouteContext>()({
  component: RootLayout,
});
